import { SelectedVariants } from '@wix/bookings-uou-types';

/**
 * Collapses per-participant single variant choices into the aggregate
 * `selectedVariants` shape the pricing preview + validation already understand:
 * identical choices (same option + choice id) are merged and their participant
 * counts summed (e.g. P0=Adult, P1=Adult, P2=Child → [{n:2, Adult}, {n:1, Child}]).
 * Participants who haven't picked yet (no choice) are skipped.
 */
export const aggregateParticipantVariants = (
  participantVariants: SelectedVariants[] | undefined,
): SelectedVariants[] => {
  const byChoice = new Map<string, SelectedVariants>();
  participantVariants?.forEach((variant) => {
    const choice = variant?.choices?.[0];
    if (!choice) {
      return;
    }
    const key = `${choice.optionId}::${choice.id}`;
    const existing = byChoice.get(key);
    if (existing) {
      existing.numberOfParticipants =
        (existing.numberOfParticipants || 0) +
        (variant.numberOfParticipants || 1);
    } else {
      byChoice.set(key, {
        numberOfParticipants: variant.numberOfParticipants || 1,
        choices: [{ ...choice }],
      });
    }
  });

  return Array.from(byChoice.values());
};

/**
 * The inverse of {@link aggregateParticipantVariants}: expands an aggregate variant
 * selection (as it arrives pre-selected from the calendar, e.g. [{n:2, Adult},
 * {n:1, Child}]) into one single-choice entry per participant ([Adult, Adult,
 * Child]). Used to seed the per-participant list from an incoming selection so the
 * participant count and per-participant pickers reflect the calendar choice.
 */
export const expandParticipantVariants = (
  selectedVariants: SelectedVariants[] | undefined,
): SelectedVariants[] => {
  const expanded: SelectedVariants[] = [];
  selectedVariants?.forEach((variant) => {
    const count = variant?.numberOfParticipants || 0;
    const choice = variant?.choices?.[0];
    for (let index = 0; index < count; index++) {
      expanded.push({
        numberOfParticipants: 1,
        choices: choice ? [{ ...choice }] : [],
      });
    }
  });
  return expanded;
};
